import { SyntheticEvent, useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrganizationDto } from '@sib/api';
import { Tooltip } from '@sib/ui';
import { paths } from '@spendbrain/config/paths';

interface OrganizationLogoContentProps {
  organization?: OrganizationDto;
}

interface ImageDimensions {
  width: number;
  height: number;
}

const MAX_LOGO_HEIGHT = 44;

export const OrganizationLogoContent = ({
  organization,
}: OrganizationLogoContentProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isImageError, setIsImageError] = useState(false);
  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const [imageDimensions, setImageDimensions] =
    useState<ImageDimensions | null>(null);

  const { width = 0, height = 0 } = organization || {};
  const finalWidth = width || imageDimensions?.width || 0;
  const finalHeight = height || imageDimensions?.height || 0;
  const aspectRatio =
    finalWidth && finalHeight ? finalWidth / finalHeight : 1.66;

  const calculatedLogoHeight = MAX_LOGO_HEIGHT;
  const calculatedLogoWidth = MAX_LOGO_HEIGHT * aspectRatio;

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const handleTextInteraction = useCallback(() => {
    const element = textRef.current;
    if (!element) return;

    setIsTextOverflowing(
      element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
  }, []);

  return (
    <Link to={paths.dashboard} className="hidden sm:block">
      {organization?.fileUrl && !isImageError ? (
        <Tooltip
          TooltipTriggerProps={{ asChild: true }}
          content={organization.name}
        >
          <img
            src={organization?.fileUrl}
            alt="Company Logo"
            className="size-full object-fill"
            onLoad={handleImageLoad}
            onError={() => setIsImageError(true)}
            style={{
              height: calculatedLogoHeight,
              width: calculatedLogoWidth,
              maxHeight: MAX_LOGO_HEIGHT,
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip
          TooltipTriggerProps={{ asChild: true }}
          content={isTextOverflowing && organization?.name}
        >
          <p
            ref={textRef}
            onMouseEnter={handleTextInteraction}
            onFocus={handleTextInteraction}
            className="line-clamp-2 w-[260px] overflow-hidden text-base leading-5.5 font-semibold break-words text-ellipsis"
          >
            {organization?.name}
          </p>
        </Tooltip>
      )}
    </Link>
  );
};
